/**
 * DomainSettingsPage — coach-side domain configuration.
 *
 * Currently surfaces:
 *   • Character Archetypes toggle (character_archetypes_enabled)
 *     – When ON: shows the configured character classes for this domain
 *     – Calls PATCH /scenario-os/coach/domain/{domain} to persist
 *
 * Route: /coach/domain-settings/:domain
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import CoachLayout from './CoachLayout';
import { coachAPI } from '../../services/api';
import AccessDenied from '../../components/AccessDenied';
import { toast } from '../../../../shared/utils/overlays';

// ── Pill component for focus_skills / content_tags display ───────────────────
function Pill({ label, color = '#4f46e5' }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: 11,
      fontWeight: 600,
      background: `${color}22`,
      color,
      border: `1px solid ${color}44`,
      marginRight: 4,
      marginBottom: 4,
    }}>
      {label}
    </span>
  );
}

// ── Toggle switch ─────────────────────────────────────────────────────────────
function ToggleSwitch({ checked, onChange, disabled }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        width: 48,
        height: 26,
        borderRadius: 13,
        border: 'none',
        background: checked ? '#4f46e5' : '#374151',
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.2s',
        flexShrink: 0,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span style={{
        position: 'absolute',
        top: 3,
        left: checked ? 25 : 3,
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </button>
  );
}

// ── Character class card (read-only, links to PositionsPage for editing) ──────
function CharacterClassCard({ cls }) {
  const focusSkills = cls.focus_skills || [];
  const contentTags = cls.content_tags || [];
  const quizProfile = cls.quiz_weight_profile || {};
  const easy = Math.round((quizProfile.easy ?? 0.33) * 100);
  const medium = Math.round((quizProfile.medium ?? 0.34) * 100);
  const hard = Math.round((quizProfile.hard ?? 0.33) * 100);

  return (
    <div style={{
      border: '1px solid var(--coach-border)',
      borderRadius: 10,
      padding: '14px 18px',
      background: 'var(--coach-card-bg)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: '1.6rem' }}>{cls.icon || '🎮'}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{cls.label || cls.class_id}</div>
          <div style={{ fontSize: 12, color: 'var(--coach-text-muted)', marginTop: 2 }}>
            {cls.description || 'No description'}
          </div>
        </div>
        {cls.is_default && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px',
            borderRadius: 10, background: '#4f46e522', color: '#818cf8',
            border: '1px solid #4f46e544',
          }}>DEFAULT</span>
        )}
      </div>

      {focusSkills.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--coach-text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Focus Skills
          </div>
          <div>{focusSkills.map((s) => <Pill key={s} label={s} color="#0ea5e9" />)}</div>
        </div>
      )}

      {contentTags.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--coach-text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Content Tags
          </div>
          <div>{contentTags.map((t) => <Pill key={t} label={t} color="#10b981" />)}</div>
        </div>
      )}

      {(focusSkills.length > 0 || contentTags.length > 0 || cls.quiz_weight_profile) && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--coach-text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Quiz Profile
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {[['Easy', easy, '#22c55e'], ['Medium', medium, '#f59e0b'], ['Hard', hard, '#ef4444']].map(([label, pct, color]) => (
              <div key={label} style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color, marginBottom: 2 }}>{label} {pct}%</div>
                <div style={{ height: 4, borderRadius: 2, background: '#1e293b', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {cls.ai_coach_persona_id && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--coach-text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            AI Coach Persona
          </div>
          <Pill label={cls.ai_coach_persona_id} color="#8b5cf6" />
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function DomainSettingsPage() {
  const { domain } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [error, setError] = useState(null);

  const [archetypesEnabled, setArchetypesEnabled] = useState(false);
  const [pendingEnabled, setPendingEnabled] = useState(null); // null = not dirty
  const [classes, setClasses] = useState([]);
  const [classSource, setClassSource] = useState('pack_default');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await coachAPI.listCharacterClasses(domain);
      const enabled = Boolean(data.character_archetypes_enabled);
      setArchetypesEnabled(enabled);
      setPendingEnabled(null);
      setClasses(data.character_classes || []);
      setClassSource(data.source || 'pack_default');
    } catch (e) {
      if (e.response?.status === 403) setAccessDenied(true);
      else setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [domain]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = (val) => {
    setPendingEnabled(val);
  };

  const handleSave = async () => {
    if (pendingEnabled === null) return;
    setSaving(true);
    try {
      await coachAPI.patchDomainSettings(domain, { character_archetypes_enabled: pendingEnabled });
      setArchetypesEnabled(pendingEnabled);
      setPendingEnabled(null);
      toast.success(pendingEnabled
        ? 'Character Archetypes enabled for this domain.'
        : 'Character Archetypes disabled.');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const displayEnabled = pendingEnabled !== null ? pendingEnabled : archetypesEnabled;
  const isDirty = pendingEnabled !== null;

  if (accessDenied) {
    return <AccessDenied domainId={domain} onBack={() => navigate('/coach')} />;
  }

  return (
    <CoachLayout title={`Domain Settings — ${domain}`}>
      <div style={{ padding: '28px 32px', maxWidth: 820 }}>

        {/* ── Breadcrumb ──────────────────────────────────────────────────── */}
        <div style={{ fontSize: 12, color: 'var(--coach-text-muted)', marginBottom: 20 }}>
          <Link to="/coach" style={{ color: 'var(--coach-primary)', textDecoration: 'none' }}>Dashboard</Link>
          {' › '}
          <span style={{ textTransform: 'capitalize' }}>{domain}</span>
          {' › '}
          <span>Domain Settings</span>
        </div>

        {error && (
          <div style={{
            background: '#7f1d1d22', border: '1px solid #ef444444',
            borderRadius: 8, padding: '10px 14px', marginBottom: 20, color: '#fca5a5', fontSize: 13,
          }}>
            ⚠ {error}
          </div>
        )}

        {loading ? (
          <div style={{ color: 'var(--coach-text-muted)', padding: '40px 0', textAlign: 'center' }}>
            Loading settings…
          </div>
        ) : (
          <>
            {/* ── CHARACTER ARCHETYPES SECTION ──────────────────────────── */}
            <div style={{
              border: '1px solid var(--coach-border)',
              borderRadius: 12,
              padding: '20px 24px',
              background: 'var(--coach-card-bg)',
              marginBottom: 24,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <h2 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700 }}>
                    🎭 Character Archetypes
                  </h2>
                  <p style={{ margin: '0 0 4px', fontSize: 13, color: 'var(--coach-text-muted)', lineHeight: 1.5 }}>
                    {displayEnabled
                      ? 'Learners are assigned to role-based learning tracks. Content with matching audience tags is shown to the right roles.'
                      : 'All learners see the same content path. Enable to offer role-based learning tracks.'}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <span style={{ fontSize: 12, color: 'var(--coach-text-muted)', fontWeight: 600 }}>
                    {displayEnabled ? 'ON' : 'OFF'}
                  </span>
                  <ToggleSwitch
                    checked={displayEnabled}
                    onChange={handleToggle}
                    disabled={saving}
                  />
                </div>
              </div>

              {/* Tip */}
              <div style={{
                background: '#4f46e511',
                border: '1px solid #4f46e533',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 12,
                color: '#a5b4fc',
                lineHeight: 1.5,
                marginBottom: isDirty ? 14 : 0,
              }}>
                💡 <strong>Tip:</strong> Enable this after you've tagged your scenes with{' '}
                <code style={{ background: '#1e293b', padding: '1px 5px', borderRadius: 3, fontSize: 11 }}>audience_tags</code>{' '}
                in the Content Builder.
              </div>

              {isDirty && (
                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={saving}
                    style={{ minWidth: 100 }}
                  >
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                  <button
                    className="btn btn-outline"
                    onClick={() => setPendingEnabled(null)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {/* ── CHARACTER CLASS LIST (when archetypes enabled) ─────────── */}
            {displayEnabled && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700 }}>Character Classes</h3>
                    <p style={{ margin: 0, fontSize: 12, color: 'var(--coach-text-muted)' }}>
                      {classSource === 'pack_default'
                        ? 'Showing domain pack defaults. Edit a class in the Positions manager to customise it.'
                        : `${classes.length} class${classes.length !== 1 ? 'es' : ''} configured for this domain.`}
                    </p>
                  </div>
                  <Link
                    to={`/coach/positions/${domain}`}
                    className="btn btn-outline btn-sm"
                    style={{ textDecoration: 'none' }}
                  >
                    Manage Classes →
                  </Link>
                </div>

                {classes.length === 0 ? (
                  <div style={{
                    textAlign: 'center', padding: '40px 20px',
                    border: '1px dashed var(--coach-border)', borderRadius: 10,
                    color: 'var(--coach-text-muted)', fontSize: 13,
                  }}>
                    No character classes defined yet.{' '}
                    <Link to={`/coach/positions/${domain}`} style={{ color: 'var(--coach-primary)' }}>
                      Add your first class →
                    </Link>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
                    {classes.map((cls) => (
                      <CharacterClassCard key={cls.class_id || cls.id} cls={cls} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </CoachLayout>
  );
}
